import { useState, useEffect, useRef } from "react";
import {
  getRechargeReport,
  getOperatorType,
  getActiveOperator,
} from "../../../services/panel/panelServices";
import toast from "../../../utils/toast";
import Pagination from "../../component/Pagination";

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

// <input type="date"> gives "2026-10-29"; the API wants "29 Oct 2026"
const toApiDate = (value) => {
  if (!value) return "";
  const [y, m, d] = value.split("-");
  return `${d} ${MONTHS[Number(m) - 1]} ${y}`;
};

// No dates picked by default — the user chooses them
const defaultFilters = () => ({
  fromDate: "",
  toDate: "",
  status: 0,
  opTypeId: 0, // serviceID from GetOpTypes, 0 = all
  oid: 0, // operator from GetOperatorsActive, 0 = all
  accountNo: "",
});

const NUMBER_FILTERS = ["status", "opTypeId", "oid"];

const REPORT_TOP_ROWS = 50;
const PAGE_SIZE = 10;
const TABLE_COLS = 13;

const formatAmount = (amt) => `₹${Number(amt || 0).toLocaleString("en-IN")}`;

// "29 Oct 2026 04:38:11:943 PM" → "29 Oct 2026, 04:38 PM" (split the string —
// the ":943" millisecond part breaks Date parsing)
const formatEntryDate = (value) => {
  const [d, m, y, time, ampm] = String(value || "").split(" ");
  if (!time) return value || "—";
  return `${d} ${m} ${y}, ${time.slice(0, 5)} ${ampm || ""}`.trim();
};

// Bill (item) status: text comes from type_, _Type only picks the colour
const STATUS_PILL = {
  0: "pill-neutral",
  1: "pill-pending",
  2: "pill-success",
  3: "pill-failed",
};

// Request Status 0 returns every status, so 0 is "All" in the filter
const STATUS_FILTERS = [
  { value: 0, label: "All" },
  { value: 1, label: "Pending" },
  { value: 2, label: "Success" },
  { value: 3, label: "Failed" },
];

export default function TransactionHistory() {
  const [isReportData, setReportData] = useState([]);
  const [reportLoading, setReportLoading] = useState(false);
  // Chrome fills the saved login mobile into text filters; it never fills a
  // readOnly input, so unlock on focus
  const [filterTextUnlocked, setFilterTextUnlocked] = useState(false);
  // Filters being edited vs. the ones last sent to the API
  const [filters, setFilters] = useState(defaultFilters);
  // API has no paging (TopRows only), so page the returned rows here
  const [pageNumber, setPageNumber] = useState(1);
  const pageStart = (pageNumber - 1) * PAGE_SIZE;
  const pageRows = isReportData.slice(pageStart, pageStart + PAGE_SIZE);

  const [isOpTypeData, setOpTypeData] = useState([]);
  const [isOperatorData, setOperatorData] = useState([]);
  const [operatorLoading, setOperatorLoading] = useState(false);
  const latestOpTypeRef = useRef(0);

  useEffect(() => {
    fetchOpTypes();
    fetchRechargeReport(defaultFilters());
  }, []);

  // IsRecent stays true until the user searches with changed filters
  const fetchRechargeReport = async (f) => {
    setReportLoading(true);
    try {
      const isRecent = JSON.stringify(f) === JSON.stringify(defaultFilters());
      const payload = {
        FromDate: toApiDate(f.fromDate),
        ToDate: toApiDate(f.toDate),
        Status: f.status,
        OID: f.oid,
        OpTypeID: f.opTypeId,
        AccountNo: f.accountNo.trim(),
        TopRows: REPORT_TOP_ROWS,
        IsRecent: isRecent,
        IsExport: false,
      };
      const res = await getRechargeReport(payload);

      if (res?.statuscode === 1) {
        setReportData(res?.rechargeReport || []);
      } else {
        setReportData([]);
      }
      setPageNumber(1);
    } catch (err) {
      console.log(
        err.response?.data?.msg || "Failed to fetch transaction history",
        "error",
      );
    } finally {
      setReportLoading(false);
    }
  };

  const fetchOpTypes = async () => {
    try {
      const res = await getOperatorType();

      if (res?.statuscode === 1) {
        setOpTypeData(res?.data?.assignedOpTypes || []);
      }
    } catch (err) {
      console.log(
        err.response?.data?.msg || "Failed to fetch operator types",
        "error",
      );
    }
  };

  const fetchOperators = async (opTypeId) => {
    latestOpTypeRef.current = opTypeId;
    setOperatorData([]);
    if (!opTypeId) return;
    setOperatorLoading(true);
    try {
      const res = await getActiveOperator({ OType: opTypeId });

      if (latestOpTypeRef.current !== opTypeId) return;

      // This API returns the list in `operators`, not in `data`
      if (res?.statuscode === 1) {
        setOperatorData(res?.operators || []);
      }
    } catch (err) {
      console.log(
        err.response?.data?.msg || "Failed to fetch operators",
        "error",
      );
    } finally {
      if (latestOpTypeRef.current === opTypeId) setOperatorLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    const parsed = NUMBER_FILTERS.includes(name) ? Number(value) : value;
    setFilters((f) => ({
      ...f,
      [name]: parsed,
      // Operators belong to one type — clear the pick when the type changes
      ...(name === "opTypeId" ? { oid: 0 } : {}),
    }));
    if (name === "opTypeId") fetchOperators(parsed);
  };

  const handleSearch = () => {
    const { fromDate, toDate } = filters;
    if (fromDate && toDate && fromDate > toDate) {
      toast.error("From date can't be after To date.");
      return;
    }
    fetchRechargeReport(filters);
  };

  const handleReset = () => {
    const defaults = defaultFilters();
    setFilters(defaults);
    fetchOperators(0);
    fetchRechargeReport(defaults);
  };

  return (
    <>
      <div className="screen active" id="screen-history">
        <div className="page-head">
          <div>
            <h1>Transaction history</h1>
            <div className="ph-sub">
              Every payment made from this corporate account
            </div>
          </div>
        </div>
        <div className="card">
          <div className="form-grid form-grid-4">
            <div className="field">
              <label>From date</label>
              <input
                type="date"
                name="fromDate"
                value={filters.fromDate}
                onChange={handleFilterChange}
              />
            </div>
            <div className="field">
              <label>To date</label>
              <input
                type="date"
                name="toDate"
                value={filters.toDate}
                onChange={handleFilterChange}
              />
            </div>
            <div className="field">
              <label>Status</label>
              <select
                name="status"
                value={filters.status}
                onChange={handleFilterChange}
              >
                {STATUS_FILTERS.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label>Operator type</label>
              <select
                name="opTypeId"
                value={filters.opTypeId}
                onChange={handleFilterChange}
              >
                <option value={0}>All</option>
                {isOpTypeData.map((t) => (
                  <option key={t.serviceID} value={t.serviceID}>
                    {t.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label>Operator</label>
              <select
                name="oid"
                value={filters.oid}
                onChange={handleFilterChange}
                disabled={!filters.opTypeId || operatorLoading}
              >
                <option value={0}>
                  {operatorLoading
                    ? "Loading operators..."
                    : !filters.opTypeId
                      ? "Select operator type first"
                      : "All"}
                </option>
                {isOperatorData.map((o) => (
                  <option key={o.oid} value={o.oid}>
                    {o.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label>Account no</label>
              <input
                name="accountNo"
                value={filters.accountNo}
                onChange={handleFilterChange}
                placeholder="Consumer / account number"
                autoComplete="off"
                readOnly={!filterTextUnlocked}
                onFocus={() => setFilterTextUnlocked(true)}
              />
            </div>
            <div
              className="field"
              style={{ display: "flex", alignItems: "flex-end", gap: 10 }}
            >
              <button
                type="button"
                className="btn btn-ghost"
                onClick={handleReset}
                disabled={reportLoading}
              >
                Reset
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSearch}
                disabled={reportLoading}
              >
                {reportLoading ? "Searching..." : "Search"}
              </button>
            </div>
          </div>
        </div>
        <div className="card" style={{ marginTop: 20 }}>
          <div className="table-wrap table-scroll">
            <table
              className="data-table"
              id="history-table"
              style={{ whiteSpace: "nowrap" }}
            >
              <tbody>
                <tr>
                  <th>S.No</th>
                  <th>Transaction ID</th>
                  <th>Account</th>
                  <th>Operator</th>
                  <th className="tright">Opening</th>
                  <th className="tright">Amount</th>
                  <th className="tright">Debit</th>
                  <th className="tright">Comm</th>
                  <th className="tright">Closing</th>
                  <th>Status</th>
                  <th>Refund status</th>
                  <th>Live ID</th>
                  <th>Date</th>
                </tr>
                {reportLoading ? (
                  <tr>
                    <td colSpan={TABLE_COLS} style={{ textAlign: "center" }}>
                      Loading...
                    </td>
                  </tr>
                ) : isReportData.length === 0 ? (
                  <tr>
                    <td colSpan={TABLE_COLS} style={{ textAlign: "center" }}>
                      No transactions found.
                    </td>
                  </tr>
                ) : (
                  pageRows.map((tx, i) => (
                    <tr key={tx.tid}>
                      <td className="mono">{pageStart + i + 1}</td>
                      <td className="mono" style={{ fontSize: 11 }}>
                        {tx.transactionID}
                      </td>
                      <td className="mono">{tx.account}</td>
                      <td>
                        {tx.operator}
                        {tx.display1 && (
                          <div
                            style={{
                              fontSize: "10.5px",
                              color: "var(--text-faint)",
                            }}
                          >
                            {tx.display1}
                          </div>
                        )}
                      </td>
                      <td className="tright mono">
                        {formatAmount(tx.lastBalance)}
                      </td>
                      <td className="tright mono">
                        {formatAmount(tx.requestedAmount)}
                      </td>
                      <td className="tright mono">{formatAmount(tx.amount)}</td>
                      <td className="tright mono">
                        {formatAmount(tx.commission)}
                      </td>
                      <td className="tright mono">
                        {formatAmount(tx.balance)}
                      </td>
                      <td>
                        <span
                          className={`pill ${STATUS_PILL[tx._Type] || "pill-neutral"}`}
                        >
                          {tx.type_}
                        </span>
                      </td>
                      <td>{tx.refundStatus_ || "—"}</td>
                      <td className="mono" style={{ fontSize: 11 }}>
                        {tx.liveID || "—"}
                      </td>
                      <td>{formatEntryDate(tx.entryDate)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {!reportLoading && (
            <Pagination
              pageNumber={pageNumber}
              pageSize={PAGE_SIZE}
              totalRecords={isReportData.length}
              onChange={setPageNumber}
              label="transactions"
            />
          )}
        </div>
      </div>
    </>
  );
}
